import { LngLatBounds } from 'mapbox-gl'
import { MapRef } from 'react-map-gl'

import { i18nTProp } from '@flowwai-frontend/shared/common-components'
import { GeoLocationDetails } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import {
   GeoLocationErrorEnum,
   MAPS_NO_DATA_LOCATION,
   MAPS_ZOOM_VALUES
} from '../constants'
import { MapCoordinates } from '../types'

export const getStringifiedCoordinates = (
   coordinates?: MapCoordinates[]
): string => coordinates?.map(({ lat, lng }) => `${lat}${lng}`).join(':') ?? ''

export const fitAllCoordinatesInMap = (
   map: mapboxgl.Map | MapRef,
   coordinates?: MapCoordinates[],
   showExtrusion?: boolean
) => {
   const bounds = new LngLatBounds()

   if (coordinates?.length) {
      coordinates?.forEach(({ lat, lng }) => bounds.extend([lng, lat]))
      map.fitBounds(bounds, {
         padding: 50,
         maxZoom: 18,
         duration: 0,
         pitch: showExtrusion ? 55 : 0
      })
   } else {
      bounds.extend([MAPS_NO_DATA_LOCATION.lng, MAPS_NO_DATA_LOCATION.lat])
      map.fitBounds(bounds, {
         padding: 50,
         maxZoom: MAPS_ZOOM_VALUES.noDataZoom,
         duration: 0,
         pitch: 0
      })
   }
}

export const fitAllCoordinatesInGoogleMap = (
   map: google.maps.Map,
   coordinates?: MapCoordinates[]
) => {
   const bounds = new google.maps.LatLngBounds()

   if (coordinates?.length) {
      coordinates?.forEach(eachCoordinate => bounds.extend(eachCoordinate))

      map.fitBounds(bounds, 50)
   } else {
      bounds.extend(MAPS_NO_DATA_LOCATION)
      map.fitBounds(bounds, 50)
   }
}

export const getGeoLocationErrorMessage = (
   latError: GeoLocationErrorEnum | null,
   lngError: GeoLocationErrorEnum | null,
   t: i18nTProp
): string | null => {
   if (!latError && !lngError) return null

   const isLatRequired = latError === GeoLocationErrorEnum.Required
   const isLatInvalid = latError === GeoLocationErrorEnum.Invalid

   const isLngRequired = lngError === GeoLocationErrorEnum.Required
   const isLngInvalid = lngError === GeoLocationErrorEnum.Invalid

   if (latError && lngError) {
      if (isLatRequired && isLngRequired)
         return t('errorMessages.latitudeAndLongitudeAreRequired')

      if (isLatInvalid && isLngInvalid)
         return t('errorMessages.invalidLatitudeAndLongitude')

      if (isLatRequired && isLngInvalid)
         return `${t('errorMessages.latitudeIsRequired')} & ${t(
            'errorMessages.invalidLongitude'
         )}`

      if (isLatInvalid && isLngRequired)
         return `${t('errorMessages.invalidLatitude')} & ${t(
            'errorMessages.longitudeIsRequired'
         )}`
   }

   switch (true) {
      case isLatRequired:
         return t('errorMessages.latitudeIsRequired')
      case isLngRequired:
         return t('errorMessages.longitudeIsRequired')
      case isLatInvalid:
         return t('errorMessages.invalidLatitude')
      case isLngInvalid:
         return t('errorMessages.invalidLongitude')
   }

   return null
}

export const redirectToGoogleMaps = (latitude: number, longitude: number) => {
   window.open(
      `https://www.google.com/maps?q=${latitude},${longitude}`,
      '_blank',
      'noopener,noreferrer'
   )
}

export const getCurrentPositionFromLocation = (
   currentLocation: GeoLocationDetails
) => ({
   lat: currentLocation.latitude,
   lng: currentLocation.longitude
})

export const redirectToGoogleMapsWithCenterMarked = (
   coordinates: MapCoordinates | MapCoordinates[],
   zoomLevel: number,
   streetView = true
): void => {
   const coordsArray = Array.isArray(coordinates) ? coordinates : [coordinates]

   if (coordsArray.length === 0) return
   let centerLat = 0
   let centerLng = 0

   coordsArray.forEach(coord => {
      centerLat += coord.lat
      centerLng += coord.lng
   })
   centerLat /= coordsArray.length
   centerLng /= coordsArray.length

   let url = `https://www.google.com/maps/place/${centerLat},${centerLng}/@${centerLat},${centerLng},${zoomLevel}z`

   if (streetView) url += '/data=!3m1!1e3'

   if (coordsArray.length > 1) {
      coordsArray.forEach((coord, index) => {
         url += `&markers=color:red|label:${String.fromCharCode(65 + index)}|${
            coord.lat
         },${coord.lng}`
      })
   }

   window.open(url, '_blank', 'noopener,noreferrer')
}

export const redirectToGoogleEarth = (coordinates: MapCoordinates): void => {
   const url = `https://earth.google.com/web/@${coordinates.lat},${coordinates.lng},10000d`

   window.open(url, '_blank', 'noopener,noreferrer')
}

export const createOlaMapsCustomAttributionForGoogleMaps = (): HTMLAnchorElement => {
   const olaAttribution = document.createElement('a')
   olaAttribution.href = 'https://openstreetmap.org/copyright'
   olaAttribution.target = '_blank'
   olaAttribution.rel = 'noopener noreferrer'
   olaAttribution.innerHTML = 'Ola Maps'
   olaAttribution.style.background = '#ffffffb3'
   olaAttribution.style.fontSize = '10px'
   olaAttribution.style.padding = '1px 6px'

   return olaAttribution
}
